import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Typography, CardContent, Card, Box, Grid } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { CreateFormMeetings } from '../../../components/meetings/CreateFormMeetings';
import { fetchServiceById, insertDataAttachment, insertDataService, updateDataService } from '../../../services/meetings.service';
import { DialogSelectCompany } from '../../../components/dialogs/DialogSelectCompany';
import LoadingOverlay from '../../../components/others/LoadingOverlay';
import { getCompanies } from '../../../services/service.service';
import { useForm } from '../../../hooks/useForm';
import { TableAttachments } from '../../../components/meetings/TableAttachments';
import { ActionButtons } from '../../../components/others/ActionButtons';
import { useNotification } from '../../../hooks/useNotification';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

export const LayoutMeetings = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [idEdit, setIdEdit] = useState('');

    const [openDialogCompany, setOpenDialogCompany] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [attachment, setAttachment] = useState([]);
    const [values, handleInputChange, handleDirectInputChange, setAllInfo] = useForm({
        id_company: "",
        id_contact: "",
        meeting_type: "",
        id_commercial_opportunity: "",
        followed_up_by: [],
        meeting_objective: "",
        meeting_status: "",
        address: "",
        comments: "",
        meeting_date: moment(new Date()).format('YYYY-MM-DDTHH:mm'),
    });
    const formService = useRef();
    const navigate = useNavigate();
    const [{ userSession }] = useDataAuth();
    const location = useLocation();
    const [sendNotification] = useNotification();

    const fetchDataService = useCallback(async (id) => {
        setIsEdit(true);
        setLoading(true);
        setIdEdit(id);
        try {
            const response = await fetchServiceById(userSession.token, id);
            if (response.status === 200) {
                const dto = response.data;
                dto.meeting_date = dto.meeting_date.replace(':00Z', '');
                dto.followed_up_by = response.data.followed_up_by ? response.data.followed_up_by.split(',').filter(c => c) : [];
                setAllInfo(dto);
            }
        } catch (error) {
            console.error("LayoutMeetings EDIT fetchDataService Error fetching service data:", error);
        } finally {
            setLoading(false);
        }
    }, [userSession.token]);

    useEffect(() => {
        if (location.state?.config) {
            const { config } = location.state;
            if (config.data?.id_commercial_visit) {
                fetchDataService(config.data.id_commercial_visit);
            } else {
                const fetchCompanies = async () => {
                    try {
                        setLoading(true);
                        const companiesResponse = await getCompanies('35', userSession.token);
                        if (companiesResponse?.data?.length === 1) {
                            handleConfirmAction(companiesResponse.data[0]);
                        } else {
                            setOpenDialogCompany(true);
                            setCompanies(companiesResponse?.data || []);
                        }
                    } catch (e) {
                        console.error("LayoutMeetings useEffect:", e.message);
                    } finally {
                        setLoading(false);
                    }
                }
                fetchCompanies();
            }
        }
    }, [fetchDataService, location.state, userSession.token]);

    const handleConfirmAction = useCallback(async ({ id_company }) => {
        handleDirectInputChange('id_company', id_company);
        setOpenDialogCompany(false);
    }, []);

    const saveAttachment = async (data, id) => {
        const res = await insertDataAttachment({
            id_flow: "900114098-API-UPLOAD_ATTACHMENT_S3_SAVE_DB",
            id_company: values.id_company,
            name: data.name.replace(/\s+/g, '_'),
            file: data.bytes,
            id_user_action: "MENU-MEETING",
            id_unique: id,
        },
            userSession.token
        );
        return res;
    }

    const submitDataMeeting = async (eValues) => {
        setLoading(true);
        try {
            const formattedValues = {
                ...eValues,
                followed_up_by: Array.isArray(eValues.followed_up_by)
                    ? eValues.followed_up_by.join(",")
                    : eValues.followed_up_by,
            };
            const msgList = [];
            if (isEdit) {
                const response = await updateDataService(formattedValues, userSession.token);
                if (response.status === 200) {
                    for (const item of attachment) {
                        if (!item?.added)
                            continue;

                        try {
                            const resp = await saveAttachment(item, idEdit);
                            if (!(resp.status === 200 && resp.data?.RowsAffected && parseInt(resp.data.RowsAffected, 10) > 0)) {
                                msgList.push(item.name);
                            }
                        } catch (error) {
                            console.log('aca error ', error);
                            msgList.push(item.name);
                        }
                    }
                    sendNotification({
                        msg: t("messages.saveSuccess"),
                        variant: "success",
                    });
                }
            } else {
                const response = await insertDataService(formattedValues, userSession.token);
                if (response.status === 200 && Array.isArray(response.data) && response.data[0]?.id?.trim()) {
                    if (attachment.length > 0) {
                        for (const item of attachment) {
                            try {
                                const resp = await saveAttachment(item, response.data[0]?.id?.trim());
                                if (!(resp.status === 200 && resp.data?.RowsAffected && parseInt(resp.data.RowsAffected, 10) > 0)) {
                                    msgList.push(item.name);
                                }
                            } catch (error) {
                                msgList.push(item.name);
                            }
                        }
                    }
                    sendNotification({
                        msg: t("messages.saveSuccess"),
                        variant: "success",
                    });
                }
            }
            if (msgList.length > 0)
                sendNotification({
                    msg: t('messages.attachmentUploadFailed', { files: msgList.join(', ') }),
                    variant: "error",
                });
            cancelAction();
        } catch (e) {
            console.error("LayoutMeetings submitDataMeeting response: ", e.message);
        }
        setLoading(true);
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        const isValidFormContact = formService.current?.validateForm();
        if (!isValidFormContact) {
            sendNotification({
                msg: t("validation.requiredFieldsMissing"),
                variant: "error",
            });
            return
        }
        submitDataMeeting(values);
    };

    const cancelAction = () => navigate(-1);

    return (
        <Box sx={{ width: '100%', mx: 'auto' }}>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        {t("meeting.tracing")}
                    </Typography>
                    <CreateFormMeetings
                        ref={formService}
                        dto={values}
                        handleInputChange={handleInputChange}
                        handleDirectInputChange={handleDirectInputChange}
                        setLoading={setLoading}
                        isEdit={isEdit}
                    />

                    <Grid container spacing={1} sx={{ mt: 2 }}>
                        <TableAttachments
                            attachment={attachment}
                            setAttachment={setAttachment}
                            edit={isEdit}
                            id={idEdit}
                        />
                    </Grid>

                    <Grid size={{ xs: 12 }} sx={{ mt: 4, textAlign: 'right' }}>
                        <ActionButtons
                            onCancel={cancelAction}
                            onSubmit={handleSubmit}
                        />
                    </Grid>
                </CardContent>
            </Card>

            <DialogSelectCompany
                openDialogConfirm={openDialogCompany}
                handleCancelConfirm={cancelAction}
                handleConfirmAction={handleConfirmAction}
                data={companies}
            />
            <LoadingOverlay isOpen={loading} />
        </Box>
    );
};